<template>
  <div class="flex flex-col">
    <div v-if="search" class="w-full border-b dark:border-gray-800 relative">
      <text-field v-model="query" :placeholder="$t('general.filter')" leading="search" border="" shadow="" />
      <div class="absolute right-0 transform mr-2 top-1/2 -translate-y-1/2">
        <o-button
          v-if="query.length"
          size="xs"
          variant="danger"
          transparent
          flat
          icon="x"
          @click="clearQuery()"
        />
      </div>
    </div>

    <div class="flex-shrink flex-grow h-1/2 text-base leading-6 focus:outline-none sm:text-sm sm:leading-5">
      <o-scroll class="pb-2 max-h-80">
        <div
          v-if="selectAll"
          :class="selectAllClass"
        >
          <o-checkbox
            v-model="selectAllOptions"
            :name="name"
            :required="required"
            :disabled="disabled"
            :status="status"
            :size="size"
            :label="selectAllLabel"
            :base-class="checkboxBaseClass"
            :option-class="optionClass"
            class="mb-2"
          />
        </div>

        <div class="my-2 px-2">
          <div
            v-for="(item, index) in filtered"
            :key="`${ id || name }-${index}`"
            :class="item.class"
          >
            <o-checkbox
              :id="`${ id || name || key }-${index}`"
              :key="`${ id || name }-${index}`"
              v-model="currentValue"
              :value="item.value"
              :name="name"
              :required="required"
              :disabled="disabled"
              :status="status"
              :size="size"
              :label="item.text || item.label"
              :info="item.info"
              :control="item.control"
              :base-class="checkboxBaseClass"
              :option-class="optionClass"
              @blur="onBlur"
              @focus="onFocus"
            >
              <slot name="option" :option="item" />
            </o-checkbox>
          </div>
        </div>
      </o-scroll>
    </div>
  </div>
</template>

<script setup lang="ts">
import Fuse from 'fuse.js'
import get from 'lodash/get'

import OCheckbox from './Checkbox.vue'
import TextField from './Text.vue'

const { $i18n } = useNuxtApp()

interface Props {
  disabled?: boolean
  search?: boolean
  name?: string | null
  id?: string | null
  modelValue?: any[] | Record<string, any> | string | number
  required?: boolean
  baseClass?: string | Record<string, any> | string[]
  checkboxBaseClass?: string | Record<string, any> | string[]
  defaultStatusClass?: string | Record<string, any> | string[]
  warningStatusClass?: string | Record<string, any> | string[]
  errorStatusClass?: string | Record<string, any> | string[]
  successStatusClass?: string | Record<string, any> | string[]
  disabledClass?: string | Record<string, any> | string[]
  defaultSizeClass?: string | Record<string, any> | string[]
  largeSizeClass?: string | Record<string, any> | string[]
  smallSizeClass?: string | Record<string, any> | string[]
  optionClass?: string | Record<string, any> | string[]
  inputClass?: string | Record<string, any> | string[]
  labelClass?: string | Record<string, any> | string[]
  imageClass?: string | Record<string, any> | string[]
  selectAll?: boolean
  selectAllLabel?: string
  selectAllClass?: string | Record<string, any> | string[]
  // multi mixin
  valueAttribute?: string
  textAttribute?: string
  options?: any[] | Record<string, any>
  // classes mixin
  size?: string | null
  status?: boolean | string | null
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  search: false,
  name: null,
  id: null,
  modelValue: () => [],
  required: false,
  baseClass: 'flex flex-col',
  checkboxBaseClass: undefined,
  defaultStatusClass: 'bg-transparent',
  warningStatusClass: 'border-yellow-400 bg-yellow-100',
  errorStatusClass: 'border-red-300 bg-red-100 dark:bg-red-400/20',
  successStatusClass: 'border-green-300 bg-green-100',
  disabledClass: 'cursor-not-allowed opacity-75',
  defaultSizeClass: '',
  largeSizeClass: 'text-lg',
  smallSizeClass: 'text-sm',
  optionClass: 'py-1 px-2 mb-1 flex items-center rounded transition duration-200 cursor-pointer bg-gray-50 dark:bg-gray-900',
  inputClass: '',
  labelClass: 'w-full flex justify-between items-center leading-none',
  imageClass: 'h-8 w-8 rounded-full',
  selectAll: false,
  selectAllLabel: undefined,
  selectAllClass: 'my-2 px-2 border-b dark:border-gray-800 font-semibold',
  valueAttribute: undefined,
  textAttribute: undefined,
  options: () => [],
  size: null,
  status: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'change', value: any): void
  (e: 'blur', value: any): void
  (e: 'focus', value: any): void
}>()

const key = ref(`${Date.now()}-${Math.random()}`)
const currentValue = ref<any>(props.modelValue)
const query = ref('')

// -- multi mixin --
function guessOptionValue (option: any) {
  if (props.valueAttribute) return get(option, props.valueAttribute)
  return get(option, 'value', get(option, 'id', get(option, 'text')))
}

function guessOptionText (option: any) {
  if (props.textAttribute) return get(option, props.textAttribute)
  return get(option, 'text', get(option, 'label'))
}

function guessInfoText (option: any) {
  return get(option, 'info', get(option, 'description'))
}

function guessColour (option: any) {
  const colour = get(option, 'colour', get(option, 'background'))
  return colour ? (colour.charAt(0) === '#' ? colour : '#' + colour) : null
}

function normalizeOption (option: any): any {
  if (['string', 'number', 'boolean'].includes(typeof option)) {
    return { value: option, text: option }
  }
  return {
    value: guessOptionValue(option),
    text: guessOptionText(option),
    info: guessInfoText(option),
    colour: guessColour(option),
    control: option.control,
    disabled: option.disabled,
    tooltip: option.tooltip,
    class: option.class,
  }
}

const normalizedOptions = computed(() => {
  if (props.options) {
    if (Array.isArray(props.options)) {
      return props.options.map((option: any) => normalizeOption(option))
    } else {
      return Object.keys(props.options).map(key => ({
        value: key,
        text: (props.options as Record<string, any>)[key],
      }))
    }
  }
  return []
})

// -- classes mixin --
const noStatus = computed(() => props.status === null)
const isError = computed(() => props.status === false || props.status === 'error')
const isSuccess = computed(() => props.status === true || props.status === 'success')
const isWarning = computed(() => props.status === 'warning')

const statusClasses = computed(() => {
  const classes: any[] = []
  if (props.disabled) {
    if (props.disabledClass) classes.push(props.disabledClass)
  }
  if (props.size === null && props.defaultSizeClass) classes.push(props.defaultSizeClass)
  else if (props.size === 'sm' && props.smallSizeClass) classes.push(props.smallSizeClass)
  else if (props.size === 'lg' && props.largeSizeClass) classes.push(props.largeSizeClass)
  if (!props.disabled && noStatus.value && props.defaultStatusClass) classes.push(props.defaultStatusClass)
  if (isError.value && props.errorStatusClass) classes.push(props.errorStatusClass)
  else if (isSuccess.value && props.successStatusClass) classes.push(props.successStatusClass)
  else if (isWarning.value && props.warningStatusClass) classes.push(props.warningStatusClass)
  return classes
})

const currentClass = computed(() => {
  const classes: any[] = [props.baseClass]
  return classes.concat(statusClasses.value)
})

const filtered = computed(() => {
  const items = normalizedOptions.value

  if (query.value.length) {
    const fuse = new Fuse(items, {
      keys: ['text', 'info', 'value'],
    })
    const results = fuse.search(query.value)
    return results.map(({ item }) => item)
  }

  return items
})

const optionValues = computed(() => {
  return normalizedOptions.value
    .map((option: any) => JSON.stringify(option))
})

const selectAllOptions = computed({
  get () {
    const value = currentValue.value
    const options = normalizedOptions.value

    return !!value.length &&
      !!options.every(function (v: any) {
        return value.includes(v.value)
      })
  },
  set (value: boolean) {
    if (!value) {
      const options = normalizedOptions.value.map((o: any) => o.value)
      currentValue.value = currentValue.value.filter((v: any) => !options.includes(v))
    } else {
      const options = normalizedOptions.value.map((o: any) => o.value)
      currentValue.value = [...new Set([...currentValue.value, ...options])]
    }
  },
})

watch(() => props.modelValue, (value) => {
  currentValue.value = value
})

watch(currentValue, (val) => {
  emit('update:modelValue', val)
  emit('change', val)
})

function clearQuery () {
  query.value = ''
}

function onBlur (e: Event) {
  emit('blur', e)
}

function onFocus (e: Event) {
  emit('focus', e)
}
</script>
